import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import { styled } from "@mui/material/styles";
import { blue, blueGrey, green, red } from "@mui/material/colors";

const INACTIVE_LABEL_COLOR = blueGrey[500];

const getVariantColors = (variant) => {
  switch (variant) {
    case "success":
      return {
        activeBackground: green[100],
        completedBackground: green[50],
        activeTextColor: green[900],
        completedTextColor: green[900],
      };
    case "error":
      return {
        activeBackground: red[100],
        completedBackground: red[50],
        activeTextColor: red[900],
        completedTextColor: red[900],
      };
    default:
      return {
        activeBackground: blue[100],
        completedBackground: blue[50],
        activeTextColor: blue[900],
        completedTextColor: blue[900],
      };
  }
};

const CustomStep = styled(Step)(({ variant = "default", ownerState }) => {
  const colors = getVariantColors(variant);

  return {
    "& .MuiStepLabel-root": {
      padding: "7px 16px",
      // padding: "6px 16px",
      paddingLeft: "16px",
      paddingRight: 0,
      zIndex: 111,
      whiteSpace: "nowrap",
      height: "100%", // ← add
      alignItems: "center", // ← add
    },
    ".MuiStepLabel-label": {
      height: "100%",
      color: INACTIVE_LABEL_COLOR,
      "> .MuiBox-root": {
        paddingTop: 0,
        paddingBottom: "0 !important",
        // paddingBottom: 0,
      },
      "&.Mui-completed": {
        color: colors.completedTextColor,
        fontWeight: 400,
      },
      "&.Mui-active": {
        fontWeight: 800,
        color: colors.activeTextColor,
      },
    },
    "& .MuiStepLabel-iconContainer": {
      display: "none",
    },
    "& .MuiStepLabel-labelContainer": {
      color: ownerState.active
        ? "white"
        : ownerState.completed
          ? "white"
          : "#757575",
      fontWeight: 500,
    },
    backgroundColor: ownerState.active
      ? colors.activeBackground
      : ownerState.completed
        ? colors.completedBackground
        : "#ffffff",
    borderRadius: 0,
    "&:first-of-type": {
      borderTopLeftRadius: 8,
      borderBottomLeftRadius: 8,
    },
    "&:last-of-type": {
      borderTopRightRadius: 8,
      borderBottomRightRadius: 8,
    },
    "&:not(:last-child)::after": {
      content: '""',
      position: "absolute",
      width: 25,
      height: 25,
      borderRadius: "3px",
      backgroundColor: "inherit",
      right: "-11px",
      top: "50%",
      transform: "translateY(-50%) rotate(45deg)",
      zIndex: 1,
    },
  };
});

const CustomStepper = styled(Stepper)({
  background: "transparent",
  border: `1px solid #e2e8f0`,
  overflow: "hidden",
  zIndex: 100,
  overflowX: "auto",
  borderRadius: "8px",
  alignItems: "stretch", // ← add this
  "& .MuiStepConnector-root": { display: "none" },
  "& .MuiStep-root": {
    padding: 0,
    position: "relative",
    flex: 1,
    display: "flex", // ← add this
    alignItems: "stretch", // ← add this
  },
});
// ─── Inline Sub-Status Chips (rendered inside the active parent step) ────────

const InlineSubSteps = ({
  subStatuses,
  activeSubStep,
  variant = "default",
}) => {
  const colors = getVariantColors(variant || "default");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "3px",
        mt: 0.5,
      }}
    >
      {subStatuses.map((sub, idx) => {
        const isActive = idx === activeSubStep;
        const isCompleted = activeSubStep >= 0 && idx < activeSubStep;
        return (
          <Box
            key={sub}
            sx={{
              fontSize: "10px",
              lineHeight: 1,
              fontWeight: isActive ? 700 : 400,
              color: isActive
                ? colors.activeTextColor
                : isCompleted
                  ? colors.completedTextColor
                  : blueGrey[400],
              backgroundColor: isActive
                ? "rgba(255,255,255,0.7)"
                : isCompleted
                  ? "rgba(255,255,255,0.45)"
                  : "rgba(239,239,239,0.04)",
              borderRadius: "4px",
              px: 0.75,
              py: 0.375,
              whiteSpace: "nowrap",
              border: isActive
                ? `1px solid ${colors.activeTextColor}40`
                : "1px solid transparent",
            }}
          >
            {sub}
          </Box>
        );
      })}
    </Box>
  );
};

// ─── Main ProgressStepper ────────────────────────────────────────────────────

/**
 * @param {Object}   props
 * @param {number}   props.activeStep      – index of the active main step
 * @param {string[]} props.statusList      – list of main status labels
 * @param {string}   [props.variant]       – "default" | "success" | "error"
 * @param {Object}   [props.subStepConfig] – map of { [parentLabel]: { subStatuses: string[], activeSubStep: number } }
 *                                            Sub-steps are only shown for the currently active parent step.
 */
const ProgressStepper = ({
  activeStep,
  statusList,
  variant = "default",
  subStepConfig,
}) => {
  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowX: "auto",
        ".MuiButtonBase-root": {
          p: 0,
          m: 0,
          width: "100%", // ← add
          alignItems: "stretch", // ← add
        },
        ".MuiStepLabel-root": {
          width: "100%", // ← add
          height: "100%", // ← add
          alignItems: "stretch", // ← add
        },
        ".MuiStepLabel-labelContainer": {
          p: "0 8px",
          height: "100%", // ← add
        },
      }}
    >
      <CustomStepper nonLinear activeStep={activeStep}>
        {statusList.map((label, index) => {
          const stepSubConfig = subStepConfig?.[label];
          const showSubSteps =
            stepSubConfig?.subStatuses?.length > 0 && index === activeStep;

          return (
            <CustomStep
              key={label}
              completed={index < activeStep}
              active={index === activeStep}
              variant={variant}
              ownerState={{
                completed: index < activeStep,
                active: index === activeStep,
              }}
            >
              <StepButton sx={{ fontSize: "12px", cursor: "text" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center", // ← center horizontally over chips
                    justifyContent: "center", // ← center vertically in the cell
                    flexWrap: "nowrap",
                    height: "100%", // ← fill the button height
                    py: 1,
                  }}
                >
                  {label}
                  {showSubSteps && (
                    <InlineSubSteps
                      subStatuses={stepSubConfig.subStatuses}
                      activeSubStep={stepSubConfig.activeSubStep}
                      variant={variant}
                    />
                  )}
                </Box>
              </StepButton>
            </CustomStep>
          );
        })}
      </CustomStepper>
    </Box>
  );
};

export default ProgressStepper;
